// src-lit/components/product/kd-product-title.ts
import { LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { onEvent } from '../../utils/dom';
import { EVENT_NAMES, type VariantSelectedDetail } from '../../constants/events';
import { SELECTORS } from '../../constants/selectors';
import { getTitleForVariant, type TitleMap } from './title.logic';

/** 内容量×商品セット マップ（variantId → ラベル。空文字はバッジ hidden） */
type VolumeMap = Record<string, string>;

/**
 * @element kd-product-title
 * @description バリアント選択に応じて H1 商品タイトルと 内容量×商品セット バッジを更新する。
 *   kd-product-variant-selected イベントを購読し、SSR で埋め込まれた TitleMap / VolumeMap から
 *   該当バリアントの値を引いて textContent / hidden を更新する。
 *   （内容量バッジはバッジ行から H1 直下へ移設。更新責務も kd-product-badge-group から移った）
 */
@customElement('kd-product-title')
export class KdProductTitle extends LitElement {
  createRenderRoot() {
    return this;
  }

  private unsubscribe: (() => void) | null = null;

  private titleMap: TitleMap = {};

  private volumeMap: VolumeMap = {};

  render() {
    return nothing;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.titleMap = this.parseMap<TitleMap>(SELECTORS.PRODUCT_TITLE.TITLE_MAP);
    this.volumeMap = this.parseMap<VolumeMap>(SELECTORS.PRODUCT_TITLE.VOLUME_MAP);
    this.unsubscribe = onEvent<VariantSelectedDetail>(
      document,
      EVENT_NAMES.PRODUCT.VARIANT_SELECTED,
      (e) => this.handleVariantSelected(e.detail),
    );
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  private parseMap<T extends object>(selector: string): T {
    const script = this.querySelector<HTMLScriptElement>(selector);
    if (!script?.textContent) return {} as T;
    try {
      return JSON.parse(script.textContent) as T;
    } catch {
      return {} as T;
    }
  }

  private handleVariantSelected(detail: VariantSelectedDetail): void {
    this.updateTitle(detail.variantId);
    this.updateVolume(detail.variantId);
  }

  private updateTitle(variantId: string): void {
    const title = getTitleForVariant(this.titleMap, variantId);
    if (title === undefined) return;

    const el = this.querySelector<HTMLElement>(SELECTORS.PRODUCT_TITLE.TITLE);
    if (el) {
      el.textContent = title;
    }
  }

  /** 内容量×商品セット バッジ。ラベルが空（内容量・セット名なし）のときは hidden。 */
  private updateVolume(variantId: string): void {
    const wrapper = this.querySelector<HTMLElement>(SELECTORS.PRODUCT_TITLE.VOLUME);
    if (!wrapper) return;

    const label = this.volumeMap[variantId];
    if (label === undefined) return;

    if (label === '') {
      wrapper.hidden = true;
      return;
    }
    wrapper.hidden = false;
    const badgeSpan = wrapper.firstElementChild;
    if (badgeSpan) {
      badgeSpan.textContent = label;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-product-title': KdProductTitle;
  }
}
